import UAParser from 'ua-parser-js'

import { ENV_DEV_OR_STAGING, VUE_APP_SEMVERSION } from '@/helpers/environment'
import Sentry from '@/modules/Sentry'

import { createAnalytics, Title, type Storage } from '@simple-life-app/analytics-ts-web'

declare global {
	interface Window {
		// Assigned by an inline script in index.html BEFORE any module executes,
		// so reading it at module eval below is safe.
		language?: string
	}
}

type AnalyticsInstance = ReturnType<typeof createAnalytics>
type Ctx = NonNullable<Parameters<AnalyticsInstance['setContext']>[0]>

// Union of all event names accepted by the SDK's typed track (= keyof EventMap & string).
// Derived from the track signature because the SDK does not export EventName directly.
export type EventName = Parameters<AnalyticsInstance['track']>[0]

// Header shape for the 6 header events (BlockClick, BlockView, ButtonClick, PopupClose,
// ScreenView, PopupView) — structurally identical to the SDK's HeaderMap values.
type TrackHeader = { tracking: { title?: Title; path: Title[] } }

const apiKey: string = import.meta.env.VITE_JET_ANALYTICS_KEY ?? ''
const apiUrl: string = import.meta.env.VITE_JET_ANALYTICS_URL ?? ''

const isPreviewMode = (() => {
	if (!ENV_DEV_OR_STAGING) return false
	try {
		return new URLSearchParams(window.location.search).has('is_ob_preview')
	} catch {
		return false
	}
})()

// Analytics lib persists instance-id / session / unsent batches. Mirror the old SDK's
// localStorage-backed store (paltabrain.* keys — no collision with lscache-vuex / vuex).
const localStorageAdapter: Storage = {
	get(key) {
		try {
			return localStorage.getItem(key)
		} catch {
			return null
		}
	},
	set(key, value) {
		try {
			localStorage.setItem(key, value)
		} catch {
			/* ignore quota / disabled storage */
		}
	},
	remove(key) {
		try {
			localStorage.removeItem(key)
		} catch {
			/* ignore */
		}
	},
}

// No-op singleton matching the real `Analytics` shape — used when env vars are missing
// or in OB preview mode. Keeps call sites safe without runtime checks.
const noopAnalytics: AnalyticsInstance = {
	setContext: () => {},
	track: () => {},
	flush: () => Promise.resolve(),
}

// --- Dev/staging console logger ---------------------------------------------------
// FORMAT IS AN E2E CONTRACT (performance-web-tests/fragments/jet_event_capture.js):
// groupCollapsed's first arg must match /%cJet \[debug\]:\s*%c(\S+)/ and the payload
// OBJECT must be console.log's args[0] — a string label in front would push it to
// args[1] and silently drop the capture (Received: 0). Header is logged WITH a
// 'header:' label so it is NOT double-captured.
const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false
const EVENT_COLOR = prefersDark ? '#ffcb6b' : '#f76d47'
const CONTEXT_COLOR = prefersDark ? '#82aaff' : '#6182b8'

// Debug-log view of a payload. An empty `repeated` array encodes to ZERO wire bytes,
// i.e. it is effectively absent. Drop such arrays from the logged view so the console
// mirrors the wire (and the pre-migration Jet.js log) instead of a spurious `[]`.
function payloadForLog(payload: unknown): unknown {
	if (payload === null || typeof payload !== 'object') return payload
	const isEmptyArray = (v: unknown): boolean => Array.isArray(v) && v.length === 0
	const entries = Object.entries(payload)
	if (!entries.some(([, v]) => isEmptyArray(v))) return payload
	return Object.fromEntries(entries.filter(([, v]) => !isEmptyArray(v)))
}

type ErasedFn = (...args: unknown[]) => void

// Mutates `instance` to mirror every track()/setContext() to the DevTools console
// (KOVA attachDebugLogger pattern). Attached in dev/staging only.
function attachDebugLogger(instance: AnalyticsInstance): void {
	const origTrack = instance.track.bind(instance)
	const origSetContext = instance.setContext.bind(instance)

	const wrappedTrack: ErasedFn = (...args) => {
		/* eslint-disable no-console */
		console.groupCollapsed(`%cJet [debug]: %c${String(args[0])}`, 'font-weight: 400', `color: ${EVENT_COLOR}`)
		console.log(payloadForLog(args[1]))
		if (args[2] !== undefined) console.log('header:', args[2])
		console.groupEnd()
		/* eslint-enable no-console */
		origTrack(...args)
	}
	const wrappedSetContext: ErasedFn = (...args) => {
		/* eslint-disable no-console */
		console.groupCollapsed('%cJet [debug]: %csetContext', 'font-weight: 400', `color: ${CONTEXT_COLOR}`)
		console.log(args[0])
		console.groupEnd()
		/* eslint-enable no-console */
		origSetContext(...args)
	}

	Object.assign(instance, { track: wrappedTrack, setContext: wrappedSetContext })
}

// Wrap track() with a try/catch that routes encoder failures to Sentry. The old facade
// did this via analytics.init({ logger }) (parsed Function:/Field: → jetEventName tags);
// the new SDK exposes no logger/onError hook. ts-proto THROWS on a malformed payload —
// notably a `repeated` field passed as undefined instead of [] — and, with the old
// try/catch wrappers gone, that throw would otherwise propagate uncaught to the call site
// (and, mid page-teardown, could abort the unload-flush below). Report, never rethrow: a
// dropped analytics event must not break the funnel. (Silently-dropped UNKNOWN keys are not
// catchable here — ts-proto ignores them; that needs schema validation the SDK doesn't expose.)
function attachErrorReporting(instance: AnalyticsInstance): void {
	const origTrack = instance.track.bind(instance)
	const wrapped: ErasedFn = (...args) => {
		try {
			origTrack(...args)
		} catch (error) {
			Sentry.withScope((scope) => {
				scope.setTags({ jetError: true, jetEventName: String(args[0]) })
				scope.captureException(error instanceof Error ? error : new Error(String(error)))
			})
		}
	}
	Object.assign(instance, { track: wrapped })
}

// Persist buffered events before the page is torn down. The SDK only writes batches to
// localStorage inside flush() (after a 1000ms debounce), buffering events in memory until
// then. A hard navigation before the debounce fires — e.g. the download screen's
// `window.location.href` 300ms after tracking funnel-completion events — would drop
// everything still buffered. flush() persists synchronously (Batcher.flush → persistBatches),
// so triggering it on page-hide makes those events durable: the in-flight fetch may be
// cancelled by the navigation, but recoverPersistedBatches resends them on the next init.
// Restores the old palta SDK's IN_DB persist-on-track + sendAllBatches-on-init guarantee
// that the in-memory-first analytics-ts-web SDK otherwise loses.
function attachUnloadFlush(instance: AnalyticsInstance): void {
	const flush = (): void => {
		void instance.flush()
	}
	// visibilitychange('hidden') is the reliable cross-platform teardown signal (fires on
	// mobile background / tab-switch and before pagehide); pagehide covers the actual unload.
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') flush()
	})
	window.addEventListener('pagehide', flush)
}

// Replicate the old palta client's setDefaultContext(): parse the UA with ua-parser-js
// and auto-fill os / device / browser (+ app_platform), each field only when defined.
// ua-parser-js (NOT KOVA's `platform` lib) keeps the C_os_* / C_device_* / C_browser_*
// context fields byte-identical to the pre-migration SDK.
function defaultContext(): Ctx {
	const ua = new UAParser(window.navigator.userAgent).getResult()

	const os: NonNullable<Ctx['os']> = {}
	if (ua.os.name !== undefined) os.os_name = ua.os.name
	if (ua.os.version !== undefined) os.os_version = ua.os.version

	const device: NonNullable<Ctx['device']> = {}
	if (ua.device.vendor !== undefined) device.device_brand = ua.device.vendor
	if (ua.device.model !== undefined) device.device_model = ua.device.model

	const browser: NonNullable<Ctx['browser']> = {}
	if (ua.browser.name !== undefined) browser.browser_name = ua.browser.name
	if (ua.browser.version !== undefined) browser.browser_version = ua.browser.version

	return {
		application: {
			app_id: 'WebFunnel',
			app_platform: 'Web',
			app_version: VUE_APP_SEMVERSION,
			app_language: window.language,
		},
		os,
		device,
		browser,
	}
}

// --- Composition root (KOVA pattern, med-web-funnel/src/modules/analytics.ts) ------
// The SDK is imported statically and the instance is created synchronously at module
// eval: `Jet.track` IS the SDK's own typed track — no wrapper layer, no buffering.
// SDK init is throw-safe on corrupted localStorage (recover/session parses are
// guarded inside the SDK), so bad storage state can't crash module-eval creation.
// Sole residual construction-time throw path: crypto.getRandomValues (SDK session
// id) in a non-secure context — never prod (https) or localhost (secure-exempt).
function createInstance(): AnalyticsInstance {
	if (isPreviewMode || !apiKey || !apiUrl) return noopAnalytics
	const instance = createAnalytics({ apiKey, apiUrl, storage: localStorageAdapter })
	if (ENV_DEV_OR_STAGING) attachDebugLogger(instance)
	attachErrorReporting(instance)
	instance.setContext(defaultContext())
	attachUnloadFlush(instance)
	return instance
}

const Jet = createInstance()

// --- string-Title-key → numeric Title (runtime-title header helpers) ---------------
const TITLE_BY_KEY = new Map<string, Title>(
	Object.entries(Title).filter((entry): entry is [string, Title] => typeof entry[1] === 'number'),
)
function toTitle(key: string | undefined): Title | undefined {
	return key === undefined ? undefined : TITLE_BY_KEY.get(key)
}

export function titleEnumExists(key: string): boolean {
	return TITLE_BY_KEY.has(key)
}

/**
 * Build a typed header from runtime string Title keys — for header events whose
 * title/path come from a variable (renderer schema, computed, method arg) and so
 * can't use `Title.KEY` literals. Unknown keys map to `undefined` and are dropped.
 */
export function trackingHeader(titleKey?: string, pathKeys: string[] = []): TrackHeader {
	return {
		tracking: {
			title: toTitle(titleKey),
			path: pathKeys.map((k) => toTitle(k)).filter((t): t is Title => t !== undefined),
		},
	}
}

// --- camel→snake + value-normalize (Long/Decimal/Date → primitive) -----------------
// Backs trackDynamic(); reproduces the old palta SDK's convertCamelToSnake
// (@simple-life-app/analytics client.js: `str.replace(/([A-Z-1-9])/g, ' $1')…`) so
// dynamic-name events stay byte-identical — INCLUDING digits 1-9, which the old regex
// split on (`focusAreas2` → `focus_areas_2`, not `focus_areas2`).
function snakeKey(k: string): string {
	return k.replace(/[A-Z1-9]/g, (m) => '_' + m.toLowerCase())
}
function normalizeValue(v: unknown): unknown {
	if (v instanceof Date) return v.getTime()
	if (v === null || typeof v !== 'object') return v
	// Probe Long (toNumber) / Decimal (toString) objects from dynamic-name sites.
	// eslint-disable-next-line no-restricted-syntax
	const obj = v as { toNumber?: () => number; toString?: () => string; constructor?: { name?: string } }
	if (obj.constructor?.name === 'Decimal' && typeof obj.toString === 'function') return obj.toString()
	if (typeof obj.toNumber === 'function') return obj.toNumber()
	return v
}
function snakePayload(p: Record<string, unknown> | undefined): Record<string, unknown> {
	const out: Record<string, unknown> = {}
	if (!p) return out
	for (const [k, v] of Object.entries(p)) out[snakeKey(k)] = normalizeValue(v)
	return out
}

/**
 * Track an event whose NAME is only known at runtime (renderer-forwarded events,
 * measure-error dispatch). Converts the payload camel→snake + normalizes values.
 * Prefer the typed `Jet.track('LiteralName', {...})` everywhere else.
 */
export function trackDynamic(name: EventName, payload?: Record<string, unknown>): void {
	// Boundary cast: dynamic names can't satisfy the SDK's literal-keyed generic track.
	// eslint-disable-next-line no-restricted-syntax
	;(Jet.track as unknown as (name: string, payload: object) => void)(name, snakePayload(payload))
}

export { Title }
export default Jet
